import React from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Drawer,
  Flex,
  TextArea,
  toast,
} from "@bdo/cornerstone-ui";
import { useShareFeedbackMutation } from "@/app/feedback/feedbackApi";
import { useAppDispatch } from "@/hooks";
import {
  closeFeedback,
  openFeedback,
  selectIsFeedbackOpen,
} from "@/features/overlays/feedbackSlice";

export function FeedbackPanel() {
  const dispatch = useAppDispatch();
  const isFeedbackOpen = useSelector(selectIsFeedbackOpen);
  const { t } = useTranslation();
  const [error, setError] = React.useState<{
    title: string;
    message: string;
  } | null>(null);

  const feedbackSchema = z.object({
    feedback: z
      .string()
      .trim()
      .min(3, t("feedback.textArea.minLengthError"))
      .max(2000, t("feedback.textArea.maxLengthError")),
  });

  const { control, handleSubmit, formState, reset } = useForm({
    defaultValues: {
      feedback: "",
    },
    reValidateMode: "onChange",
    resolver: zodResolver(feedbackSchema),
  });

  const [shareFeedback, { isLoading }] = useShareFeedbackMutation();

  async function onSubmit(data: { feedback: string }) {
    try {
      const url = globalThis.location.href;
      const comment = data.feedback;
      await shareFeedback({ url, comment }).unwrap();
      toast.success({
        title: t("feedback.notification.success.title"),
        message: t("feedback.notification.success.message"),
        duration: 3000,
      });
      dispatch(closeFeedback());
      reset();
    } catch (_error) {
      console.error("Error sharing feedback:", _error);
      setError({
        title: t("feedback.notification.error.title"),
        message: t("feedback.notification.error.message"),
      });
    }
  }

  function handleClose() {
    dispatch(closeFeedback());
    setError(null);
    reset();
  }

  function setIsOpen(isOpen: boolean) {
    if (isOpen) {
      dispatch(openFeedback());
    } else {
      handleClose();
    }
  }

  return (
    <Drawer
      isOpen={isFeedbackOpen}
      setIsOpen={setIsOpen}
      shouldCloseOnOverlayClick
      title={t("feedback.title")}
      size="lg"
      padding="lg"
    >
      <Flex direction="column" gap="md" className="px-[3px]">
        <Flex gap="md" align="center" justify="between"></Flex>
        <p>{t("feedback.description")}</p>
        <form
          className="flex flex-col gap-md"
          onSubmit={handleSubmit((data) =>
            onSubmit(data as { feedback: string }),
          )}
        >
          <Controller
            name="feedback"
            control={control}
            render={({ field }) => (
              <TextArea
                label={t("feedback.textArea.label")}
                hideLabel
                placeholder={t("feedback.textArea.placeholder")}
                rows={10}
                value={field.value}
                onChange={field.onChange}
                errorMessage={formState.errors?.feedback?.message}
                maxLength={2000}
              />
            )}
          />

          {error && (
            <Alert
              variant="error"
              title={error.title}
              description={error.message}
              isCloseable={false}
            />
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            className="w-full justify-center"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t("feedback.submitButtonText")}
          </Button>
        </form>
      </Flex>
    </Drawer>
  );
}
